import React, { useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Tabs,
    Tab,
    Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';

interface SignatureInputProps {
    open: boolean;
    onClose: () => void;
    onSave: (signature: string) => void | Promise<void>;
    loading?: boolean;
    title?: string;
    confirmMessage?: string;
    initialTab?: number;
    hideTabs?: boolean;
    /** When true, only the Draw Signature tab is shown; Upload Image is hidden. */
    drawOnly?: boolean;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`signature-tabpanel-${index}`}
            aria-labelledby={`signature-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
};

const SignatureInput: React.FC<SignatureInputProps> = ({
    open,
    onClose,
    onSave,
    loading = false,
    title = "Add Signature",
    confirmMessage = "Are you sure you want to send the Email?",
    initialTab = 0,
    hideTabs = false,
    drawOnly = false
}) => {
    // When drawOnly is set, always start on the Draw tab (index 0)
    const effectiveInitialTab = drawOnly ? 0 : initialTab;
    const [signatureImage, setSignatureImage] = useState<string | null>(null);
    const [tabValue, setTabValue] = useState(effectiveInitialTab);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingSignature, setPendingSignature] = useState<string | null>(null);
    const [confirmSuccess, setConfirmSuccess] = useState(false);
    const [confirmError, setConfirmError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setValidationError(null);
        // Clear canvas when switching tabs
        if (canvasRef.current) {
            const context = canvasRef.current.getContext('2d');
            if (context) {
                context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                setHasDrawn(false);
            }
        }
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValidationError(null);
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            if (typeof reader.result === 'string') {
                setSignatureImage(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const startDrawing = (event: React.MouseEvent<HTMLCanvasElement>) => {
        setValidationError(null);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;
        const rect = canvas.getBoundingClientRect();
        context.beginPath();
        context.moveTo(event.clientX - rect.left, event.clientY - rect.top);
        setIsDrawing(true);
        setHasDrawn(true);
    };

    const draw = (event: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;
        const rect = canvas.getBoundingClientRect();
        context.lineWidth = 2;
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.strokeStyle = '#000000';
        context.lineTo(event.clientX - rect.left, event.clientY - rect.top);
        context.stroke();
    };

    const stopDrawing = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;
        context.closePath();
        setIsDrawing(false);
    };

    const clearCanvas = () => {
        setValidationError(null);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;
        context.clearRect(0, 0, canvas.width, canvas.height);
        setHasDrawn(false);
    };

    const handleSaveSignature = () => {
        let signatureData: string | null = null;

        if (tabValue === 1 && signatureImage) {
            // Image upload mode
            signatureData = signatureImage;
        } else if (tabValue === 0) {
            // Drawing mode
            if (!hasDrawn) {
                setValidationError('Please draw your signature in the canvas below before confirming.');
                return;
            }
            const canvas = canvasRef.current;
            if (canvas) {
                signatureData = canvas.toDataURL('image/png');
            }
        }

        if (signatureData) {
            // Store the signature and show confirmation dialog
            setPendingSignature(signatureData);
            setConfirmSuccess(false);
            setConfirmError(null);
            setValidationError(null);
            setShowConfirmDialog(true);
        } else {
            setValidationError('Please upload an image or draw your signature before confirming.');
        }
    };

    const handleConfirmSend = async () => {
        if (!pendingSignature) return;

        setConfirmError(null);
        setConfirmSuccess(false);

        try {
            await onSave(pendingSignature);
            setConfirmSuccess(true);

            // Close confirm (and then the signature modal) after a short delay
            setTimeout(() => {
                handleClose();
            }, 1500);
        } catch (e: any) {
            setConfirmError(e?.message || 'Failed to send email.');
        }
    };

    const handleCancelSend = () => {
        setShowConfirmDialog(false);
        setPendingSignature(null);
        setConfirmSuccess(false);
        setConfirmError(null);
    };

    const handleClose = () => {
        setSignatureImage(null);
        setTabValue(effectiveInitialTab);
        clearCanvas();
        setShowConfirmDialog(false);
        setPendingSignature(null);
        setConfirmSuccess(false);
        setConfirmError(null);
        setValidationError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold', bgcolor: '#48CAE4', color: 'white' }}>
                {showConfirmDialog ? "Confirm Action" : title}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                {showConfirmDialog ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                        {confirmSuccess ? (
                            <Alert severity="success">
                                Action completed successfully
                            </Alert>
                        ) : confirmError ? (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {confirmError}
                            </Alert>
                        ) : (
                            <Typography variant="h6" sx={{ mb: 3 }}>
                                {confirmMessage}
                            </Typography>
                        )}

                        {!confirmSuccess && !confirmError && pendingSignature && (
                            <Box sx={{ mt: 2, mb: 3 }}>
                                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                    Signature Preview:
                                </Typography>
                                <img
                                    src={pendingSignature}
                                    alt="Signature Preview"
                                    style={{ maxWidth: '100%', maxHeight: 150, border: '1px solid #ddd', borderRadius: 4 }}
                                />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <React.Fragment>
                        {validationError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {validationError}
                            </Alert>
                        )}
                        {!hideTabs && (
                            <>

                                {/* When drawOnly, render only the Draw Signature tab; keep all tab logic intact */}
                                {!drawOnly && (
                                    <Tabs
                                        value={tabValue}
                                        onChange={handleTabChange}
                                        aria-label="signature input tabs"
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            mb: 2,
                                            '& .MuiTabs-indicator': { bgcolor: '#48CAE4' },
                                            '& .MuiTab-root.Mui-selected': { color: '#48CAE4' }
                                        }}
                                    >
                                        <Tab label="Draw Signature" icon={<EditIcon />} iconPosition="start" />
                                        <Tab label="Upload Image" icon={<CloudUploadIcon />} iconPosition="start" />
                                    </Tabs>
                                )}
                            </>
                        )}

                        <TabPanel value={tabValue} index={1}>
                            <Box>
                                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                                    Click the button below to upload a signature image from your device.
                                </Typography>
                                <Box sx={{ textAlign: 'center' }}>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                    />
                                    <Button
                                        variant="contained"
                                        component="label"
                                        startIcon={<CloudUploadIcon />}
                                        sx={{
                                            bgcolor: '#48CAE4',
                                            mb: 2,
                                            '&:hover': {
                                                bgcolor: '#0077B6'
                                            }
                                        }}
                                    >
                                        Choose Image
                                        <input
                                            hidden
                                            accept="image/*"
                                            type="file"
                                            onChange={handleImageUpload}
                                        />
                                    </Button>
                                </Box>
                                {signatureImage && (
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            Preview:
                                        </Typography>
                                        <img
                                            src={signatureImage}
                                            alt="Signature Preview"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: 200,
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </TabPanel>

                        <TabPanel value={tabValue} index={0}>
                            <Box>
                                <canvas
                                    ref={canvasRef}
                                    width={400}
                                    height={200}
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    style={{
                                        border: '2px solid #48CAE4',
                                        borderRadius: '4px',
                                        cursor: 'crosshair',
                                        display: 'block',
                                        margin: '0 auto',
                                        backgroundColor: '#f9f9f9',
                                    }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={clearCanvas}
                                        sx={{
                                            color: '#48CAE4',
                                            borderColor: '#48CAE4',
                                            '&:hover': {
                                                borderColor: '#0077B6',
                                                color: '#0077B6',
                                                bgcolor: 'rgba(72, 202, 228, 0.08)'
                                            }
                                        }}
                                    >
                                        Clear Canvas
                                    </Button>
                                </Box>
                            </Box>
                        </TabPanel>
                    </React.Fragment>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                {showConfirmDialog ? (
                    <React.Fragment>
                        <Button onClick={handleCancelSend} disabled={loading}>
                            {confirmSuccess ? "Close" : "Back"}
                        </Button>
                        {!confirmSuccess && (
                            <Button
                                onClick={handleConfirmSend}
                                variant="contained"
                                sx={{
                                    bgcolor: '#48CAE4',
                                    '&:hover': {
                                        bgcolor: '#0077B6'
                                    }
                                }}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : 'Confirm'}
                            </Button>
                        )}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <Button onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveSignature}
                            variant="contained"
                            sx={{
                                bgcolor: '#48CAE4',
                                '&:hover': {
                                    bgcolor: '#0077B6'
                                }
                            }}
                            disabled={loading}
                        >
                            Save
                        </Button>
                    </React.Fragment>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SignatureInput;